import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { motion } from 'framer-motion';
import React from 'react';

import { AnimatePresence, ErrorMessage, ExpandContent, StaggerItem } from './Animations';
import { cn } from './cn';

export interface FileUploadProps {
  label?: string;
  fileExtensions?: string[];
  maxFileSize?: number;
  maxSize?: number;
  multiple?: boolean;
  uploadCallback: (files: File[]) => void;
  className?: string;
}

export const FileUpload = ({
  label,
  fileExtensions,
  maxFileSize,
  maxSize,
  multiple = false,
  uploadCallback,
  className,
}: FileUploadProps) => {
  const [files, setFiles] = React.useState<File[]>([]);
  const [error, setError] = React.useState<string>();
  const [isDragOver, setIsDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const acceptStr = fileExtensions?.map((ext) => (ext.startsWith('.') ? ext : `.${ext}`)).join(',');

  const validateAndAdd = (newFiles: FileList | File[]) => {
    setError(undefined);
    const fileArray = Array.from(newFiles);

    if (fileExtensions?.length) {
      const invalid = fileArray.find(
        (f) => !fileExtensions.some((ext) => f.name.toLowerCase().endsWith(ext.startsWith('.') ? ext : `.${ext}`))
      );
      if (invalid) {
        setError(`File "${invalid.name}" has an unsupported extension. Supported: ${fileExtensions.join(', ')}`);
        return;
      }
    }

    if (maxFileSize) {
      const tooBig = fileArray.find((f) => f.size > maxFileSize * 1024 * 1024);
      if (tooBig) {
        setError(`File "${tooBig.name}" exceeds the maximum size of ${maxFileSize} MB.`);
        return;
      }
    }

    const updated = multiple ? [...files, ...fileArray] : fileArray;

    if (maxSize) {
      const totalSize = updated.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > maxSize * 1024 * 1024) {
        setError(`Total file size exceeds the maximum of ${maxSize} MB.`);
        return;
      }
    }

    setFiles(updated);
    uploadCallback(updated);
  };

  const removeFile = (index: number) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    uploadCallback(updated);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) validateAndAdd(e.dataTransfer.files);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      <motion.div
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 transition-all duration-200 cursor-pointer',
          isDragOver
            ? 'border-info-500 bg-info-50/50 scale-[1.01]'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50/50'
        )}
        whileHover={{ scale: 1.005 }}
        animate={isDragOver ? { scale: 1.01 } : { scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <ArrowUpTrayIcon className="h-8 w-8 text-gray-400" />
        <div className="text-sm text-gray-500">
          <span className="font-medium text-info-500 hover:text-info-600 transition-colors">Click to upload</span> or
          drag and drop
        </div>
        {fileExtensions && <p className="text-xs text-gray-400 m-0">Supported: {fileExtensions.join(', ')}</p>}
        {(maxFileSize || maxSize) && (
          <p className="text-xs text-gray-400 m-0">
            {maxFileSize && `Max ${maxFileSize} MB per file`}
            {maxFileSize && maxSize && ' | '}
            {maxSize && `Max ${maxSize} MB total`}
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          aria-label={label || 'File upload'}
          accept={acceptStr}
          multiple={multiple}
          onChange={(e) => {
            if (e.target.files?.length) validateAndAdd(e.target.files);
            e.target.value = '';
          }}
        />
      </motion.div>
      <AnimatePresence>
        {error && <ErrorMessage className="text-xs text-danger-500 m-0">{error}</ErrorMessage>}
      </AnimatePresence>
      <AnimatePresence>
        {files.length > 0 && (
          <ExpandContent className="flex flex-col gap-1.5">
            {files.map((file, idx) => (
              <StaggerItem
                key={`${file.name}-${idx}`}
                className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm"
                index={idx}
                stagger={0.05}
                direction="left"
              >
                <span className="flex-1 truncate text-gray-700">{file.name}</span>
                <span className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(idx);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer bg-transparent border-none rounded-md hover:bg-gray-200 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </StaggerItem>
            ))}
          </ExpandContent>
        )}
      </AnimatePresence>
    </div>
  );
};
